/**
 * Unified Cache Manager for SwipeUI Wikipedia Service
 * Manages article content, metadata, and lookup caching with advanced features
 */

import { SwipeArticle, CacheEntry, CacheStats, ArticleId } from '../types';
import { IdResolver } from '../IdResolver';
import { LookupCache } from './LookupCache';

/**
 * Cache manager configuration
 */
export interface CacheManagerOptions {
  maxSize?: number;                    // Maximum number of articles to cache
  defaultTTL?: number;                 // Default TTL in milliseconds
  lookupCacheSize?: number;           // Size for page ID ↔ title lookup cache
  enableCompression?: boolean;         // Enable content compression (future)
  enableMetrics?: boolean;             // Track performance metrics
  cleanupInterval?: number;            // Automatic cleanup interval
}

/**
 * Cache key types for different data categories
 */
type CacheKey = string;

/**
 * Cache entry with extended metadata
 */
interface ExtendedCacheEntry extends CacheEntry<SwipeArticle> {
  contentHash?: string;                // Hash for content deduplication
  compressionRatio?: number;           // If compression is enabled
  category: 'full' | 'metadata-only' | 'content-only';
}

/**
 * Advanced cache manager with multi-tier caching and optimization
 */
export class UnifiedCacheManager {
  private articleCache = new Map<CacheKey, ExtendedCacheEntry>();
  private lookupCache: LookupCache;
  
  private stats = {
    hits: 0,
    misses: 0,
    sets: 0,
    evictions: 0,
    compressionSavings: 0,
    deduplicationSavings: 0,
  };

  private options: Required<CacheManagerOptions>;
  private cleanupTimer?: NodeJS.Timeout;

  constructor(options: CacheManagerOptions = {}) {
    this.options = {
      maxSize: options.maxSize || 1000,
      defaultTTL: options.defaultTTL || 300000, // 5 minutes
      lookupCacheSize: options.lookupCacheSize || 2000,
      enableCompression: options.enableCompression || false,
      enableMetrics: options.enableMetrics !== false,
      cleanupInterval: options.cleanupInterval || 300000, // 5 minutes
    };

    this.lookupCache = new LookupCache({
      maxSize: this.options.lookupCacheSize,
      defaultTTL: 3600000, // 1 hour for lookups
      enableMetrics: this.options.enableMetrics,
    });

    if (this.options.cleanupInterval > 0) {
      this.startCleanupTimer();
    }
  }

  /**
   * Get article from cache
   */
  get(id: ArticleId): SwipeArticle | null {
    const key = this.normalizeKey(id);
    const entry = this.articleCache.get(key);
    const now = Date.now();

    if (!entry || entry.expires < now) {
      if (this.options.enableMetrics) this.stats.misses++;
      
      if (entry) {
        this.articleCache.delete(key);
      }
      return null;
    }

    // Update access tracking
    entry.accessCount++;
    entry.lastAccessed = now;
    
    if (this.options.enableMetrics) this.stats.hits++;
    return entry.data;
  }

  /**
   * Set article in cache with optimization
   */
  set(
    id: ArticleId,
    article: SwipeArticle, 
    options: {
      ttl?: number;
      category?: 'full' | 'metadata-only' | 'content-only';
      skipDeduplication?: boolean;
    } = {}
  ): void {
    const key = this.normalizeKey(id);
    const now = Date.now();
    const ttl = options.ttl || this.options.defaultTTL;

    // Update lookup cache if we have page ID and title
    this.updateLookupCache(article);

    // Check for deduplication opportunities
    let dedupSavings = 0;
    if (!options.skipDeduplication && article.content) {
      dedupSavings = this.handleDeduplication(article);
    }

    // Evict if necessary
    if (this.shouldEvict()) {
      this.evictLeastRecentlyUsed();
    }

    // Calculate entry size
    const estimatedSize = this.calculateArticleSize(article);

    // Create cache entry
    const entry: ExtendedCacheEntry = {
      data: article,
      timestamp: now,
      expires: now + ttl,
      accessCount: 1,
      lastAccessed: now,
      size: estimatedSize,
      category: options.category || 'full',
    };

    this.articleCache.set(key, entry);

    if (this.options.enableMetrics) {
      this.stats.sets++;
      if (dedupSavings > 0) {
        this.stats.deduplicationSavings += dedupSavings;
      }
    }
  }

  /**
   * Get multiple articles efficiently
   */
  getMultiple(ids: ArticleId[]): Map<ArticleId, SwipeArticle | null> {
    const results = new Map<ArticleId, SwipeArticle | null>();
    
    for (const id of ids) {
      results.set(id, this.get(id));
    }
    
    return results;
  }

  /**
   * Set multiple articles efficiently
   */
  setMultiple(articles: Array<{ id: ArticleId; article: SwipeArticle; ttl?: number }>): void {
    for (const { id, article, ttl } of articles) {
      this.set(id, article, { ttl });
    }
  }

  /**
   * Check if article exists in cache
   */
  has(id: ArticleId): boolean {
    const key = this.normalizeKey(id);
    const entry = this.articleCache.get(key);
    return entry ? entry.expires > Date.now() : false;
  }

  /**
   * Delete article from cache
   */
  delete(id: ArticleId): boolean {
    const key = this.normalizeKey(id);
    const deleted = this.articleCache.delete(key);
    
    // Also try to clean up from lookup cache if it's a page/title ID
    try {
      const parsed = IdResolver.parse(id);
      if (parsed.type === 'page') {
        const pageId = parseInt(parsed.value, 10);
        this.lookupCache.deleteByPageId(pageId);
      } else if (parsed.type === 'title') {
        this.lookupCache.deleteByTitle(parsed.value);
      }
    } catch {
      // Ignore parsing errors for delete operations
    }
    
    return deleted;
  }

  /**
   * Clear all caches
   */
  clear(): void {
    this.articleCache.clear();
    this.lookupCache.clear();
    
    if (this.options.enableMetrics) {
      this.stats = {
        hits: 0,
        misses: 0,
        sets: 0,
        evictions: 0,
        compressionSavings: 0,
        deduplicationSavings: 0,
      };
    }
  }

  /**
   * Get comprehensive cache statistics
   */
  getStats(): CacheStats & {
    lookupStats: ReturnType<LookupCache['getStats']>;
    evictions: number;
    compressionSavings: number;
    deduplicationSavings: number;
    categories: Record<string, number>;
  } {
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? this.stats.hits / totalRequests : 0;
    
    // Calculate memory usage and categorize entries
    let memoryUsage = 0;
    const categories: Record<string, number> = {};
    
    for (const [key, entry] of this.articleCache.entries()) {
      memoryUsage += entry.size || 0;
      categories[entry.category] = (categories[entry.category] || 0) + 1;
    }
    
    return {
      size: this.articleCache.size,
      hits: this.stats.hits,
      misses: this.stats.misses,
      hitRate,
      memoryUsage,
      lookupStats: this.lookupCache.getStats(),
      evictions: this.stats.evictions,
      compressionSavings: this.stats.compressionSavings,
      deduplicationSavings: this.stats.deduplicationSavings,
      categories,
    };
  }

  /**
   * Resolve article ID to full Wikipedia page ID and title
   */
  async resolveId(id: ArticleId): Promise<{ pageId?: number; title?: string; fromCache: boolean }> {
    try {
      const parsed = IdResolver.parse(id);
      
      if (parsed.type === 'page') {
        const pageId = parseInt(parsed.value, 10);
        const cachedTitle = this.lookupCache.getTitleByPageId(pageId);
        return {
          pageId,
          title: cachedTitle || undefined,
          fromCache: !!cachedTitle,
        };
      }
      
      if (parsed.type === 'title') {
        const title = parsed.value;
        const cachedPageId = this.lookupCache.getPageIdByTitle(title);
        return {
          pageId: cachedPageId || undefined,
          title,
          fromCache: !!cachedPageId,
        };
      }
      
      // Temp IDs don't have Wikipedia page IDs
      return { fromCache: false };
      
    } catch {
      return { fromCache: false };
    }
  }

  /**
   * Update lookup cache with article data
   */
  updateLookupCache(article: SwipeArticle): void {
    if (!article.metadata?.pageId || !article.title) {
      return;
    }

    this.lookupCache.set(article.metadata.pageId, article.title);
  }

  /**
   * Prefetch articles that are likely to be needed
   */
  prefetchArticles(ids: ArticleId[]): ArticleId[] {
    const missingIds: ArticleId[] = [];
    
    for (const id of ids) {
      if (!this.has(id)) {
        missingIds.push(id);
      }
    }
    
    return missingIds;
  }

  /**
   * Get cache key for article ID (normalized)
   */
  private normalizeKey(id: ArticleId): CacheKey {
    return IdResolver.hash(id);
  }

  /**
   * Check if we should evict entries
   */
  private shouldEvict(): boolean {
    return this.articleCache.size >= this.options.maxSize;
  }

  /**
   * Evict least recently used entries
   */
  private evictLeastRecentlyUsed(): void {
    if (this.articleCache.size === 0) return;

    const evictCount = Math.max(1, Math.floor(this.options.maxSize * 0.1));
    const entries = Array.from(this.articleCache.entries());
    
    // Sort by last accessed (oldest first)
    entries.sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);
    
    for (let i = 0; i < evictCount && i < entries.length; i++) {
      const [key] = entries[i];
      this.articleCache.delete(key);
    }
    
    if (this.options.enableMetrics) {
      this.stats.evictions += evictCount;
    }
  }

  /**
   * Handle content deduplication (placeholder for future optimization)
   */
  private handleDeduplication(article: SwipeArticle): number {
    // Future: implement content deduplication based on content hashes
    // Return estimated space savings
    return 0;
  }

  /**
   * Calculate approximate memory size of article
   */
  private calculateArticleSize(article: SwipeArticle): number {
    let size = 200; // Base object overhead
    
    size += article.id.length * 2;
    size += article.title.length * 2;
    size += article.url.length * 2;
    
    if (article.content) {
      size += article.content.length * 2;
    }
    
    if (article.extract) {
      size += article.extract.length * 2;
    }
    
    if (article.links) {
      size += article.links.length * 100; // Approximate per link
    }
    
    return size;
  }

  /**
   * Clean up expired entries
   */
  cleanupExpired(): number {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [key, entry] of this.articleCache.entries()) {
      if (entry.expires < now) {
        this.articleCache.delete(key);
        cleanedCount++;
      }
    }
    
    // Also clean lookup cache
    cleanedCount += this.lookupCache.cleanupExpired();
    
    return cleanedCount;
  }

  /**
   * Start automatic cleanup
   */
  private startCleanupTimer(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }

    this.cleanupTimer = setInterval(() => {
      this.cleanupExpired();
    }, this.options.cleanupInterval);

    if (this.cleanupTimer.unref) {
      this.cleanupTimer.unref();
    }
  }

  /**
   * Get cache health status
   */
  getHealthStatus(): {
    healthy: boolean;
    issues: string[];
    recommendations: string[];
    stats: ReturnType<this['getStats']>;
  } {
    const stats = this.getStats();
    const issues: string[] = [];
    const recommendations: string[] = [];
    
    // Check hit rate
    if (stats.hitRate < 0.6) {
      issues.push(`Low cache hit rate: ${(stats.hitRate * 100).toFixed(1)}%`);
      recommendations.push('Consider increasing cache size or TTL');
    }
    
    // Check capacity
    if (stats.size >= this.options.maxSize * 0.9) {
      issues.push(`High cache usage: ${stats.size}/${this.options.maxSize}`);
      recommendations.push('Consider increasing maxSize or reducing TTL');
    }
    
    // Check lookup cache
    const lookupStatus = this.lookupCache.getStatusSummary();
    if (!lookupStatus.healthy) {
      issues.push(...lookupStatus.issues.map(issue => `Lookup cache: ${issue}`));
    }
    
    return {
      healthy: issues.length === 0,
      issues,
      recommendations,
      stats,
    };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
    }
    this.lookupCache.destroy();
    this.clear();
  }
}